'use client';

import { RouteFailureScreen } from '@/components/system/RouteFailureScreen';
import { useRouteFailure } from '@/components/system/useRouteFailure';
import { getRunningVersion, reloadToVersion } from '@/lib/frontend-version';

/**
 * Catches errors in the root layout. Must define its own <html>/<body>.
 * @see https://nextjs.org/docs/app/building-your-application/routing/error-handling
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const failure = useRouteFailure(error, 'Fatal global error');
  const update = failure.kind === 'update';
  const scriptFailure = update || failure.kind === 'stale-chunk';
  const title = update
    ? 'A new version was released'
    : failure.kind === 'stale-chunk'
      ? 'This page could not load its scripts'
      : failure.kind === 'checking'
        ? 'Opening this page'
        : 'This page could not open';
  const message = update
    ? 'A new version was released. Reload to fetch the latest files.'
    : failure.kind === 'stale-chunk'
      ? 'The scripts for this page failed to load. Reload to try again, or go back.'
      : failure.kind === 'checking'
        ? 'Checking whether a new version is available.'
        : error.message || 'Please reload the page.';

  return (
    <html lang="en">
      <body style={{ margin: 0 }}>
        <RouteFailureScreen
          chunkError={scriptFailure}
          title={title}
          message={message}
          onReload={() => {
            reloadToVersion(failure.deployed || getRunningVersion());
          }}
          onBack={() => {
            reset();
            window.location.assign('/');
          }}
        />
      </body>
    </html>
  );
}
